if (!customElements.get('stock-scarcity')) {
  class StockScarcity extends HTMLElement {
    connectedCallback() {
      // Mantém um valor estável por variante somente enquanto esta página vive.
      // Uma nova navegação deve gerar uma nova percepção de estoque disponível.
      this.randomCounts = new Map();
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onProductInfoLoaded = this.onProductInfoLoaded.bind(this);
      this.productForm = document.getElementById(`ProductForm-${this.dataset.sectionId}`);
      this.productForm?.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('productInfo:loaded', this.onProductInfoLoaded);
      this.refresh();
    }

    disconnectedCallback() {
      this.productForm?.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('productInfo:loaded', this.onProductInfoLoaded);
      cancelAnimationFrame(this.animationFrame);
    }

    onVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      if (this.dataset.stockSource === 'random') {
        this.render(this.randomCountFor(variant.id), variant.available, true);
      }
      else {
        this.setAttribute('aria-busy', 'true');
      }
    }

    onProductInfoLoaded() {
      this.removeAttribute('aria-busy');
      this.refresh();
    }

    refresh() {
      const state = this.querySelector('[data-stock-state]');
      if (!state) return;

      const available = state.dataset.available === 'true';
      const tracked = state.dataset.inventoryTracked === 'true';
      const count = this.dataset.stockSource === 'random'
        ? this.randomCountFor(state.dataset.variantId)
        : Math.max(0, Number(state.dataset.count) || 0);

      this.render(count, available, tracked);
    }

    render(count, available, tracked) {
      const shouldHide =
        (this.dataset.stockSource === 'real' && !tracked) ||
        (this.dataset.hideWhenUnavailable === 'true' && !available) ||
        (this.dataset.hideWhenZero === 'true' && count <= 0);

      this.hidden = shouldHide;
      if (shouldHide) return;

      const message = this.querySelector('[data-stock-message-text]');
      if (message) {
        // O HTML vem de uma tradução ou configuração escapada no Liquid.
        const template = count === 1 ? this.dataset.messageOne : this.dataset.messageOther;
        message.innerHTML = template.replaceAll('__stock_count__', String(count));
      }

      const referenceStock = Math.max(1, Number(this.dataset.referenceStock) || 1);
      const percentage = Math.min(100, Math.max(0, (count / referenceStock) * 100));
      this.style.setProperty('--stock-target-width', `${percentage}%`);

      const track = this.querySelector('[data-stock-track]');
      if (track) {
        track.setAttribute('aria-valuenow', String(count));
        track.setAttribute('aria-valuemax', String(referenceStock));
      }

      this.restartBarAnimation();
    }

    restartBarAnimation() {
      if (this.hasStartedBarAnimation) return;
      this.hasStartedBarAnimation = true;

      cancelAnimationFrame(this.animationFrame);
      this.classList.remove('is-preparing', 'is-ready');

      if (this.dataset.animateBar !== 'true') return;

      this.classList.add('is-preparing');
      void this.offsetWidth;
      this.animationFrame = requestAnimationFrame(() => this.classList.add('is-ready'));
    }

    randomCountFor(variantId) {
      // Valores não numéricos são normalizados sem quebrar a renderização.
      const parsedMin = Number(this.dataset.randomMin);
      const parsedMax = Number(this.dataset.randomMax);
      const configuredMin = Number.isFinite(parsedMin) ? parsedMin : 0;
      const configuredMax = Number.isFinite(parsedMax) ? parsedMax : configuredMin;
      const min = Math.min(configuredMin, configuredMax);
      const max = Math.max(configuredMin, configuredMax);
      const variantKey = String(variantId || 'default');

      // Reutiliza o número ao alternar e retornar para a mesma variante.
      if (this.randomCounts.has(variantKey)) return this.randomCounts.get(variantKey);

      // O limite máximo também participa do sorteio.
      const generatedValue = Math.floor(Math.random() * (max - min + 1)) + min;
      this.randomCounts.set(variantKey, generatedValue);
      return generatedValue;
    }
  }

  customElements.define('stock-scarcity', StockScarcity);
}
